import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LineHistoryIcon from 'LuxKit/Icons/line/LineHistoryIcon'
import { getCheckInCheckOutText } from 'components/Search/utils'
import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { getTypeaheadSuggestions } from 'api/search'
import GeoContext from 'contexts/geoContext'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  > * {
    height: 100%;
  }
`

const ThumbnailWrapper = styled.div`
  width: ${rem(44)};
  height: ${rem(58)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  flex-shrink: 0;
`

interface Props {
  locationName: string
  checkInDate?: string
  checkOutDate?: string
  totalAdults?: number
  selected?: boolean
  onClick?: (locationName: string, checkInDate?: string, checkOutDate?: string, totalAdults?: number) => void
}

export default function ChatLandingRecentSearchTile({
  locationName,
  checkInDate,
  checkOutDate,
  totalAdults,
  selected,
  onClick,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const [imageId, setImageId] = useState<string | undefined>()

  useEffect(() => {
    if (!locationName) return
    let cancelled = false

    getTypeaheadSuggestions(locationName, currentRegionCode, {
      types: HOTEL_SEARCH_TYPEAHEAD_TYPES,
      vertical: 'hotel',
      priority: 'next',
      limit: 1,
    }).then(results => {
      if (cancelled) return
      const firstImageId = results[0]?.format.imageId
      if (firstImageId) {
        setImageId(firstImageId)
      }
    }).catch(() => {})

    return () => { cancelled = true }
  }, [locationName, currentRegionCode])

  const recentSearchLabel = useMemo(() => intl.formatMessage({
    id: 'P6Y78uaYn-1mzaBjevwF',
    defaultMessage: 'Recent search',
    description: 'Category label for recent search tile on the chat landing page',
  }), [intl])

  const subtitle = useMemo(() => {
    const parts: Array<string> = []
    if (checkInDate && checkOutDate) {
      parts.push(getCheckInCheckOutText(checkInDate, checkOutDate))
    }
    if (totalAdults) {
      const adultsLabel = intl.formatMessage(
        {
          id: '8Wb0texqenDfYTvPGAQI',
          defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
          description: 'Pluralised adult count label for recent search tile on chat page',
        },
        { count: totalAdults },
      )
      parts.push(adultsLabel)
    }
    return parts.join(' · ')
  }, [checkInDate, checkOutDate, totalAdults, intl])

  const handleClick = useCallback(() => {
    onClick?.(locationName, checkInDate, checkOutDate, totalAdults)
  }, [onClick, locationName, checkInDate, checkOutDate, totalAdults])

  return (
    <Container onClick={handleClick}>
      <Sheet background="normal-background" border={selected ? 'normal-contrast' : 'light-foreground'} borderRadius="L" padding={12}>
        <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
          <CaptionTextBlock
            variant="small"
            weight="bold"
            format="uppercase"
            colour="light-foreground-alt"
            startIcon={<LineHistoryIcon size="XS" />}
          >
            {recentSearchLabel}
          </CaptionTextBlock>
          <Group direction="horizontal" gap={12} verticalAlign="center" grow>
            {imageId && <ThumbnailWrapper>
              <ResponsiveImage id={imageId} alt={locationName} fit="center" objectFit="cover" />
            </ThumbnailWrapper>}
            <Group direction="vertical" gap={2}>
              <BodyText variant="medium" weight="normal" lineClamp={2}>
                {locationName}
              </BodyText>
              {subtitle && <Caption variant="medium" colour="light-foreground-alt" lineClamp={1}>
                {subtitle}
              </Caption>}
            </Group>
          </Group>
        </Group>
      </Sheet>
    </Container>
  )
}
